import api, { ApiResponse } from './index'
import {
    BlogFilters,
    AdminBlogFilters,
    BlogListResponse,
    BlogDetailResponse,
    SearchResponse,
    CategoryListResponse,
    TagListResponse,
    LikeResponse,
    FeatureResponse,
    EmptyResponse,
    CreateBlogInput,
    UpdateBlogInput,
    PublishInput,
    AutosaveInput,
    PostAnalyticsResponse,
    AdminBlogListResponse,
    BlogStatsResponse
} from '@/types/blog'

export const blogApi = {
    // ===== PUBLIC ENDPOINTS =====

    /**
     * Get all published blog posts with pagination and filtering
     * @param filters - Pagination, category, tag, search, and sort options
     */
    getPublishedPosts: (filters: BlogFilters = {}): Promise<ApiResponse<BlogListResponse>> =>
        api.get('/blog', { params: filters }),

    /**
     * Get single blog post by slug
     * @param slug - The blog post slug
     */
    getBlogBySlug: (slug: string): Promise<ApiResponse<BlogDetailResponse>> =>
        api.get(`/blog/${slug}`),

    /**
     * Search blog posts
     * @param query - Search query string
     * @param filters - Additional pagination and filter options
     */
    searchBlogs: (query: string, filters: BlogFilters = {}): Promise<ApiResponse<SearchResponse>> =>
        api.get('/blog/search', { params: { q: query, ...filters } }),

    /**
     * Get blog posts by category
     * @param category - Category name
     * @param filters - Additional pagination and filter options
     */
    getByCategory: (category: string, filters: BlogFilters = {}): Promise<ApiResponse<BlogListResponse>> =>
        api.get(`/blog/category/${category}`, { params: filters }),

    /**
     * Get blog posts by tag
     * @param tag - Tag name
     * @param filters - Additional pagination and filter options
     */
    getByTag: (tag: string, filters: BlogFilters = {}): Promise<ApiResponse<BlogListResponse>> =>
        api.get(`/blog/tag/${tag}`, { params: filters }),

    /**
     * Get featured blog posts
     * @param limit - Number of featured posts to return
     */
    getFeaturedPosts: (limit: number = 5): Promise<ApiResponse<BlogListResponse>> =>
        api.get('/blog/featured', { params: { limit } }),

    /**
     * Get all available categories
     */
    getCategories: (): Promise<ApiResponse<CategoryListResponse>> =>
        api.get('/blog/categories'),

    /**
     * Get all available tags with counts
     */
    getTags: (): Promise<ApiResponse<TagListResponse>> =>
        api.get('/blog/tags/list'),

    /**
     * Like a blog post
     * @param postId - The blog post ID
     */
    likeBlog: (postId: string): Promise<ApiResponse<LikeResponse>> =>
        api.post(`/blog/${postId}/like`, {}),

    // ===== ADMIN ENDPOINTS (Require Authentication) =====

    /**
     * Get all blog posts (admin view - includes drafts and scheduled)
     * @param filters - Status, category, author, search, and sort options
     */
    getAllPosts: (filters: AdminBlogFilters = {}): Promise<ApiResponse<AdminBlogListResponse>> =>
        api.get('/admin/blog', { params: filters }),

    /**
     * Create a new blog post
     * @param data - Blog post creation data
     */
    createPost: (data: CreateBlogInput): Promise<ApiResponse<BlogDetailResponse>> =>
        api.post('/admin/blog', data),

    /**
     * Update an existing blog post
     * @param id - Blog post ID
     * @param data - Updated blog post data
     */
    updatePost: (id: string, data: UpdateBlogInput): Promise<ApiResponse<BlogDetailResponse>> =>
        api.put(`/admin/blog/${id}`, data),

    /**
     * Publish a blog post
     * @param id - Blog post ID
     * @param data - Optional scheduling data
     */
    publishPost: (id: string, data?: PublishInput): Promise<ApiResponse<BlogDetailResponse>> =>
        api.post(`/admin/blog/${id}/publish`, data || {}),

    /**
     * Toggle featured status of a blog post
     * @param id - Blog post ID
     * @param featured - Whether to feature the post
     */
    toggleFeatured: (id: string, featured: boolean = true): Promise<ApiResponse<FeatureResponse>> =>
        api.post(`/admin/blog/${id}/feature`, { featured }),

    /**
     * Archive a blog post (soft delete)
     * @param id - Blog post ID
     */
    archivePost: (id: string): Promise<ApiResponse<EmptyResponse>> =>
        api.post(`/admin/blog/${id}/archive`, {}),

    /**
     * Permanently delete a blog post
     * @param id - Blog post ID
     */
    deletePost: (id: string): Promise<ApiResponse<EmptyResponse>> =>
        api.delete(`/admin/blog/${id}`),

    /**
     * Autosave a blog post draft
     * @param id - Blog post ID
     * @param data - Partial blog post data to save
     */
    autosavePost: (id: string, data: AutosaveInput): Promise<ApiResponse<BlogDetailResponse>> =>
        api.post(`/admin/blog/${id}/autosave`, data),

    /**
     * Get blog statistics and analytics
     */
    getBlogStats: (): Promise<ApiResponse<BlogStatsResponse>> =>
        api.get('/admin/blog/stats'),

    /**
     * Get analytics for a specific blog post
     * @param id - Blog post ID
     */
    getPostAnalytics: (id: string): Promise<ApiResponse<PostAnalyticsResponse>> =>
        api.get(`/admin/blog/${id}/analytics`),
}